/**
 * RevenueCat Purchases Module
 * 
 * Complete RevenueCat integration for iOS in-app purchases.
 * Uses Firebase UID as RevenueCat app_user_id for deterministic mapping.
 * 
 * Features:
 * - SDK initialization with user's API key
 * - Paywall presentation via RevenueCatUI
 * - Customer Center for subscription management
 * - Entitlement checking for "Binit Pro"
 * - Purchase and restore functionality
 */

import { Capacitor } from '@capacitor/core';
import {
    Purchases,
    LOG_LEVEL,
    PAYWALL_RESULT,
    type CustomerInfo,
    type PurchasesPackage,
    type PurchasesOfferings,
} from '@revenuecat/purchases-capacitor';
import { RevenueCatUI } from '@revenuecat/purchases-capacitor-ui';

// ============================================================================
// Configuration
// ============================================================================

// RevenueCat Public SDK Key (iOS) - safe because it's app-specific and read-only
const REVENUECAT_API_KEY = 'test_rqWaGbhwiNQfSqnkuDjKwfIuVot';

// Entitlement ID matching RevenueCat dashboard (MUST be 'pro')
const PRO_ENTITLEMENT_ID = 'pro';

// Product identifiers (configured in App Store Connect + RevenueCat)
export const PRODUCT_IDS = {
    MONTHLY: 'monthly',
    YEARLY: 'yearly',
    LIFETIME: 'lifetime',
} as const;

// ============================================================================
// SDK State
// ============================================================================

let isInitialized = false;

// ============================================================================
// Initialization
// ============================================================================

/**
 * Initialize RevenueCat SDK.
 * Should be called once after user authentication.
 * 
 * @param firebaseUid - Firebase user UID (used as RevenueCat app_user_id)
 */
export async function initPurchases(firebaseUid: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
        console.log('RevenueCat: Skipping init on web platform');
        return;
    }

    if (isInitialized) {
        // Already initialized, just login with new user
        try {
            await Purchases.logIn({ appUserID: firebaseUid });
            console.log('RevenueCat: Logged in as', firebaseUid);
        } catch (error) {
            console.error('RevenueCat: Failed to login', error);
        }
        return;
    }

    try {
        // Enable debug logging in development
        await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });

        // Configure SDK with API key and user ID
        await Purchases.configure({
            apiKey: REVENUECAT_API_KEY,
            appUserID: firebaseUid,
        });

        isInitialized = true;
        console.log('RevenueCat: Initialized for user', firebaseUid);
    } catch (error) {
        console.error('RevenueCat: Failed to initialize', error);
        throw error;
    }
}

/**
 * Log out current RevenueCat user (call on Firebase sign out)
 */
export async function logoutPurchases(): Promise<void> {
    if (!Capacitor.isNativePlatform() || !isInitialized) return;

    try {
        await Purchases.logOut();
        console.log('RevenueCat: Logged out');
    } catch (error) {
        console.error('RevenueCat: Failed to logout', error);
    }
}

// ============================================================================
// Entitlement Checking
// ============================================================================

/**
 * Check if user has active "Binit Pro" entitlement
 */
export async function checkProEntitlement(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) return false;
    if (!isInitialized) return false;

    try {
        const { customerInfo } = await Purchases.getCustomerInfo();
        return isProActive(customerInfo);
    } catch (error) {
        console.error('RevenueCat: Failed to check entitlement', error);
        return false;
    }
}

/**
 * Get current customer info
 */
export async function getCustomerInfo(): Promise<CustomerInfo | null> {
    if (!Capacitor.isNativePlatform() || !isInitialized) return null;

    try {
        const { customerInfo } = await Purchases.getCustomerInfo();
        return customerInfo;
    } catch (error) {
        console.error('RevenueCat: Failed to get customer info', error);
        return null;
    }
}

/**
 * Check if CustomerInfo has active "Binit Pro" entitlement
 */
function isProActive(customerInfo: CustomerInfo): boolean {
    const proEntitlement = customerInfo.entitlements.active[PRO_ENTITLEMENT_ID];
    return proEntitlement?.isActive ?? false;
}

// ============================================================================
// Offerings & Packages
// ============================================================================

/**
 * Get available offerings
 */
export async function getOfferings(): Promise<PurchasesOfferings | null> {
    if (!Capacitor.isNativePlatform() || !isInitialized) return null;

    try {
        const offerings = await Purchases.getOfferings();
        return offerings;
    } catch (error) {
        console.error('RevenueCat: Failed to get offerings', error);
        return null;
    }
}

/**
 * Get available packages for purchase from current offering
 */
export async function getPackages(): Promise<PurchasesPackage[]> {
    if (!Capacitor.isNativePlatform() || !isInitialized) return [];

    try {
        const offerings = await Purchases.getOfferings();
        return offerings.current?.availablePackages ?? [];
    } catch (error) {
        console.error('RevenueCat: Failed to get packages', error);
        return [];
    }
}

// ============================================================================
// Paywall Presentation (RevenueCatUI)
// ============================================================================

/**
 * Present the RevenueCat Paywall modal.
 * Uses the paywall configured in RevenueCat dashboard.
 * 
 * @returns true if purchase/restore was successful
 */
export async function presentPaywall(): Promise<boolean> {
    if (!Capacitor.isNativePlatform() || !isInitialized) {
        console.warn('RevenueCat: Cannot present paywall - not on native or not initialized');
        return false;
    }

    try {
        const { result } = await RevenueCatUI.presentPaywall();

        switch (result) {
            case PAYWALL_RESULT.PURCHASED:
            case PAYWALL_RESULT.RESTORED:
                console.log('RevenueCat: Paywall result - success:', result);
                return true;
            case PAYWALL_RESULT.NOT_PRESENTED:
            case PAYWALL_RESULT.ERROR:
            case PAYWALL_RESULT.CANCELLED:
            default:
                console.log('RevenueCat: Paywall result:', result);
                return false;
        }
    } catch (error) {
        console.error('RevenueCat: Failed to present paywall', error);
        return false;
    }
}

/**
 * Present paywall only if user doesn't have "Binit Pro" entitlement.
 * 
 * @returns true if already entitled or purchase/restore was successful
 */
export async function presentPaywallIfNeeded(): Promise<boolean> {
    if (!Capacitor.isNativePlatform() || !isInitialized) {
        return false;
    }

    try {
        const { result } = await RevenueCatUI.presentPaywallIfNeeded({
            requiredEntitlementIdentifier: PRO_ENTITLEMENT_ID,
        });

        switch (result) {
            case PAYWALL_RESULT.PURCHASED:
            case PAYWALL_RESULT.RESTORED:
            case PAYWALL_RESULT.NOT_PRESENTED: // Already has entitlement
                return true;
            case PAYWALL_RESULT.ERROR:
            case PAYWALL_RESULT.CANCELLED:
            default:
                return false;
        }
    } catch (error) {
        console.error('RevenueCat: Failed to present paywall', error);
        return false;
    }
}

// ============================================================================
// Customer Center
// ============================================================================

/**
 * Present the Customer Center for subscription management.
 * Allows users to manage their subscription, request refunds, etc.
 */
export async function presentCustomerCenter(): Promise<void> {
    if (!Capacitor.isNativePlatform() || !isInitialized) {
        console.warn('RevenueCat: Cannot present customer center - not on native or not initialized');
        return;
    }

    try {
        await RevenueCatUI.presentCustomerCenter();
        console.log('RevenueCat: Customer center presented');
    } catch (error) {
        console.error('RevenueCat: Failed to present customer center', error);
    }
}

// ============================================================================
// Manual Purchase (alternative to paywall)
// ============================================================================

/**
 * Purchase a specific package manually.
 * Use presentPaywall() for the recommended paywall experience.
 * 
 * @param pkg - Package to purchase
 * @returns Object with success status and Pro entitlement status
 */
export async function purchasePackage(pkg: PurchasesPackage): Promise<{ success: boolean; isPro: boolean }> {
    if (!Capacitor.isNativePlatform() || !isInitialized) {
        return { success: false, isPro: false };
    }

    try {
        const { customerInfo } = await Purchases.purchasePackage({
            aPackage: pkg,
        });

        const isPro = isProActive(customerInfo);
        console.log('RevenueCat: Purchase completed, isPro:', isPro);
        return { success: true, isPro };
    } catch (error: unknown) {
        // User cancelled is not an error
        if (error && typeof error === 'object' && 'userCancelled' in error && error.userCancelled) {
            console.log('RevenueCat: User cancelled purchase');
            return { success: false, isPro: false };
        }
        console.error('RevenueCat: Purchase failed', error);
        throw error;
    }
}

/**
 * Restore previous purchases
 */
export async function restorePurchases(): Promise<{ success: boolean; isPro: boolean }> {
    if (!Capacitor.isNativePlatform() || !isInitialized) {
        return { success: false, isPro: false };
    }

    try {
        const { customerInfo } = await Purchases.restorePurchases();
        const isPro = isProActive(customerInfo);
        console.log('RevenueCat: Restore completed, isPro:', isPro);
        return { success: true, isPro };
    } catch (error) {
        console.error('RevenueCat: Restore failed', error);
        throw error;
    }
}

// ============================================================================
// Customer Info Listener
// ============================================================================

/**
 * Add a listener for customer info updates.
 * Useful for real-time entitlement changes.
 * 
 * @param callback - Called when customer info changes
 * @returns Unsubscribe function
 */
export async function addCustomerInfoListener(
    callback: (info: CustomerInfo) => void
): Promise<string | null> {
    if (!Capacitor.isNativePlatform() || !isInitialized) return null;

    try {
        const listenerId = await Purchases.addCustomerInfoUpdateListener((info) => {
            callback(info);
        });
        return listenerId;
    } catch (error) {
        console.error('RevenueCat: Failed to add listener', error);
        return null;
    }
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if running on native platform
 */
export function isNativePlatform(): boolean {
    return Capacitor.isNativePlatform();
}

/**
 * Check if SDK is initialized
 */
export function isSDKInitialized(): boolean {
    return isInitialized;
}

/**
 * Get the entitlement ID being used
 */
export function getProEntitlementId(): string {
    return PRO_ENTITLEMENT_ID;
}
